import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Benchmarking node, tornado and django for concurrency",
  "description": "",
  "published": "2011-04-15T00:00:00.000Z",
  "redirect_from": ["/blog/benchmarking-node-tornado-and-django-for-concurrency/swizec/1616"],
  "categories": "JSON, Languages, PHP, Programming, Redis, Ruby, Uncategorized",
  "hero": "./img/6sGx6.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`This is a continuation from the last time `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/comparing-clojure-and-node-js-for-speed/swizec/1593",
        "title": "Comparing clojure and node.js for speed"
      }}>{`I benchmarked node vs. clojure for hard computation`}</a>{`, sort of. This time 'round I decided to benchmark how many concurrent requests a single process of technology X can handle. Threading is fine.`}</p>
    <p>{`tl;dr ---> node is fast but a bit unpredictable, django fails a lot, tornado is mediocre`}</p>
    <p>{`Clojure isn't included in this test because it isn't your average web backend technology and I honestly didn't feel like figuring out how to set it up to act as a server ... yes, I know it's possible, no, I do not in fact know how to do it. I imagine jetty or tomcat would be the way to go.`}</p>
    <p>{`There are two very important server-side languages that I omitted. Ruby and PHP.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.ruby-lang.org/",
        "title": "Ruby (programming language)"
      }}>{`Ruby`}</a>{` , is missing simply because I don't know it well enough to implement the test in. There are varied mostly personal and silly reasons as to why this is, but the fact remains. I don't know Ruby.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.php.net/",
        "title": "PHP"
      }}>{`PHP`}</a>{` ... This isn't because I personally hate PHP (I do), I tried very very very hard to get it working for this test, but it just would not cooperate. I spent as much time trying to get PHP to `}<em parentName="p">{`run`}</em>{` as getting all the other benchmarks coded and tested. Not sure why exactly php-fpm hates me, or why it hates nginx, or maybe they've ganged up on me and hate me in unison, but it just did not work. I give up. After it finally looked like it might work it would just vomit 404 errors rather than actually run anything ... yes, I double, tripple and quintoople checked my paths.`}</p>
    <p>{`Either way, I ended up testing node.js and django, then just because the whole test is a bit unfair to django, I added Tornado, which is supposed to work a bit more the same way as node is.`}</p>
    <p>{`Onward to the test!`}</p>
    <h2 {...{
      "id": "the-benchmark-capital"
    }}>{`The `}<a parentName="h2" {...{
        "href": "http://www.benchmark.com/",
        "title": "Benchmark Capital"
      }}>{`Benchmark`}</a></h2>
    <p>{`Because this test would be completely invalid if each request didn't perform some even remotely real work I went with this flow:`}</p>
    <ol>
      <li parentName="ol">{`Read a `}<a parentName="li" {...{
          "href": "http://json.org",
          "title": "JSON"
        }}>{`JSON`}</a>{` string from raw post data`}</li>
      <li parentName="ol">{`Parse the JSON`}</li>
      <li parentName="ol">{`JSON is in the form of {key: `}{`<`}{`something>, value: `}{`<`}{`something>}`}</li>
      <li parentName="ol">{`Read the value stored at `}<em parentName="li">{`key`}</em>{` from `}<a parentName="li" {...{
          "href": "http://redis.io/",
          "title": "Redis (data store)"
        }}>{`Redis`}</a>{`.`}</li>
      <li parentName="ol">{`Store the value to key on Redis`}</li>
      <li parentName="ol">{`Return what was read from Redis`}</li>
    </ol>
    <p>{`That's basically what happens on every request. The idea being that in real life this is some sort of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Representational_State_Transfer",
        "title": "Representational State Transfer"
      }}>{`REST`}</a>{` API running somewhere, the essential tasks being parsing JSON and reading/storing to a database that is possibly an intermediary between the world-facing server and some sort of more real database (like mongo, mysql or whatever).`}</p>
    <p>{`As mentioned, I went to great pains to make sure every technology ran as a single process because otherwise it's a rather pointless test. Add very many processes, some load balancing and you can pretty much scale infinitely as long as the hardware allows it. So that's silly.`}</p>
    <h2 {...{
      "id": "the-running"
    }}>{`The Running`}</h2>
    <p>{`All the benchmarks ran on my MBP, with 4GB of RAM and a 2.5GHz core 2 duo. There were some other apps running, but they were the same every time the benchmarks ran and I didn't touch the computer during the test, so hopefully that didn't influence the result.`}</p>
    <p>{`Everything was run with a set of two scripts (written in node.js). The first spawned processes, one to ten, of which every process made 512 concurrent requests to the server. Or as concurrent as async.map is, I imagine it might be bucketed a little.`}</p>
    <p>{`Every spawned process measured the time it took for requests to finish and returned an average of the times. The main script then calculated the average of these averages, while also measuring the total time taken to run all the requests.`}</p>
    <p>{`When requests failed, the spawned process couldn't output an average time, so it was taken to be 10. There were more errors than I originally anticipated (dropped connections mostly) so there is also a graph of the fails.`}</p>
    <p>{`The main script went from 1 sub-process to 10 in one go and the server was not restarted in the mean time. Between every batch there was a two second delay to let everything cool off a bit.`}</p>
    <p>{`When a new benchmark was run the redis server was restarted and flushed of all content.`}</p>
    <p>{`edit: django was run with runfcgi, as per the `}<a parentName="p" {...{
        "href": "http://docs.djangoproject.com/en/1.3/howto/deployment/fastcgi/"
      }}>{`django docs recommendations`}</a>{` on running it behind nginx.`}</p>
    <h2 {...{
      "id": "the-code"
    }}>{`The Code`}</h2>
    <p>{`You can see the rest of the code `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/random-coding/tree/master/serverbench"
      }}>{`on github`}</a>{`. I'll just share the relevant server bits here so we can compare how much code it takes to make this sort of thing in different environments.`}</p>
    <p>{`Node:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var redis = require("redis").createClient(),
  http = require("http");

http
  .createServer(function (req, res) {
    var data = "";
    req.on("data", function (chunk) {
      data += chunk;
    });
    req.on("end", function () {
      var input = JSON.parse(data);

      redis.get(input.key, function (err, value) {
        redis.set(input.key, input.value, function (err) {
          res.writeHead(200, { "Content-Type": "application/json" });
          res.end(value);
        });
      });
    });
  })
  .listen(8124, "127.0.0.1");
`}</code></pre>
    <p><a parentName="p" {...{
        "href": "http://www.djangoproject.com",
        "title": "Django (web framework)"
      }}>{`Django`}</a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from django.http import HttpResponse

import json, redis

r = redis.Redis(host='localhost', port=6379, db=0)

def bench(request):
    data = json.loads(request.raw_post_data)
    value = r.get(data['key'])
    r.set(data['key'], data['value'])

    return HttpResponse(value)
`}</code></pre>
    <p>{`Tornado:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`import tornado.ioloop
import tornado.web

import json, redis

r = redis.Redis(host='localhost', port=6379, db=0)

class MainHandler(tornado.web.RequestHandler):
    def get(self):
        self.write("Hello, world")

    def post(self):
        data = json.loads(self.request.body)
        value = r.get(data['key'])
        r.set(data['key'], data['value'])
        self.write(value)

application = tornado.web.Application([
    (r"/", MainHandler),
])

if __name__ == "__main__":
    application.listen(8124)
    tornado.ioloop.IOLoop.instance().start()
`}</code></pre>
    <p>{`As you can see I tried to make sure nobody would be making too many connections to redis because that's a waste of time.`}</p>
    <h2 {...{
      "id": "the-results"
    }}>{`The Results`}</h2>
    <p>{`The most surprising thing I discovered is just how little effect piling on load has on all of the technologies involved. The average time for handling a request between 512 requests and 5120 is pretty much the same throughout. Even the rate of dropped requests doesn't seem to follow a really discernible pattern.`}</p>
    <p>{`Maybe I just didn't push hard enough, then again, 8 seconds average on django ... that's just horrible.`}</p>
    <p>{`Some shiny graphs:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "343px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/378f047f24f43b8f935b83062cd8d2de/41431/6sGx6.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "78.9237668161435%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'315\\'%20viewBox=\\'0%200%20400%20315\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M249%206c-1%202-2%202-4%201l-5%201h-2c-2-1-4-1-5%201h-3c-1-3-2-2-2%201s-2%204-3%201l-1-4-1%205c0%204%200%204%203%204l5-1h9l4%201c3%200%205-2%203-5v-1c2%200%202%201%202%203s0%203%202%203l2-1-1-2-1-1%201-2V6h-3m-76%202l1%205c2%204%204%204%206-1l1-3v7c0%202%204%204%207%202%202-2%201-10-2-11l-4%201h-4l-2%201c-1-2-3-2-3-1m26%204c0%205%202%205%203%201%200-3%202-4%202-1%200%204%206%206%208%202h2c0%201%201%202%203%202l2%201%202%201%201-2-1-5c0-4%200-4-3-4l-4%202h-3l-7-2h-5v5m-45%2048c-4%203-4%208%200%2011v6l-1%204h-46l-46%201h92v2l-6%2025-4%2022h-40l-1-3c-4-9-15-9-15%200v3H74l-13%201h13c11%200%2016%201%2012%202l-11%2010c-8%208-10%2010-12%209-4-2-11%205-9%2010%204%208%2016%205%2015-4-1-4%2019-24%2023-23l6-1c3-1%204-1%208%201l8%204c4%201%205%203%205%205%203%208%2015%208%2015%200%200-3%203-6%206-6l-6%2032-2%209H61l35%201h35v4c-1%206-2%206-5%206-2%200-4%201-5%203-4%204-16%203-21-1l-5-4c-3%200-7%202-8%204%200%202-1%202-9%202s-8%200-11-3c-5-5-14-2-14%205s9%2010%2014%206c3-4%2018-4%2021%200l6%202%206-2c4-5%2016-4%2021%200l4%204-4%203c-3%203-4%203-11%203s-7%200-10-3c-4-4-9-4-12%200-2%203-3%203-10%203-8%200-8%200-11-3-5-6-14-2-14%205%200%208%209%2011%2015%205%204-5%2015-5%2020%200%204%204%208%204%2012%200%203-3%203-3%2010-3s8%200%2010%203l6%203c8%200%2012-8%207-13-3-2-3-3%200-7%202-2%202-4%202-6-1-2%200-3%206-9l8-7h22l7%207c5%206%207%208%206%209l2%206c2%204%202%204%200%207-3%205-3%208%200%2011%204%203%209%203%2013-1%202-3%203-3%2010-3%206%200%207%200%2010%203%203%204%208%204%2011%201s4-8%201-11c-1-2-1-2%201-6%202-6%203-7%2011-9%207-3%208-3%2010-1%205%203%2011%200%2012-4%201-3%204-4%2010-5%203-1%2012%2023%209%2025-2%202-1%209%201%2011%203%203%208%203%2012-1%205-5%2016-5%2020%200s13%204%2015-2c0-4%2016-7%2019-5%203%203%208%203%2011%200s3-18%200-22c-3-3-8-3-12%201-3%203-18%205-20%202-1-3-6-5-9-4s-4%201-11-3l-8-5%2027-1%2028-1h-29l-29-1c0-5-4-9-9-9-3%200-7%204-7%206l-1%202c-2%200-14-33-13-35%201-1%2014%205%2014%207l2%204c3%204%208%204%2012%200%204-5%2018-8%2021-4%203%203%209%203%2011%200s3-3%2011-2c6%200%208%201%209%202%203%208%2016%205%2015-3-2-7-8-9-13-5-3%203-3%203-11%202-8%200-9-1-12-4-3-4-7-4-10%200l-3%204-4%201-8%201c-4%201-4%201-7-1-4-3-7-4-10-2-4%203-21-5-21-11h46l46-1h-47c-45%200-46%200-46-2v-4c6-8-5-17-12-11-2%203-3%208-1%2012%201%201%202%202%201%203-1%202-4%202-38%202h-37l-2-5-9-43%2095-1%2096-1H165v-4c-1-3-1-5%201-7%205-9-5-17-12-10m3%2030a11176%2011176%200%2001-8%2039c1%201%203-1%203-4s4-7%208-7c3%200%207%204%207%207l2%202c2%201%201%200-2-14l-4-21c-3-12-4-13-6-2m-49%2043l6%203c5%202%206%202%209%201h7c2%201%203%201%207-1%207-3%205-4-12-4l-17%201m42%200c-3%200-4%203-6%2015l-4%2018c-4%2015-4%2014%200%2014%205%200%2012-7%2012-12%200-4%203-8%208-8%203%200%207%204%207%209%200%203%201%204%204%207%206%205%2011%206%2011%202a1980%201980%200%2001-8-36l-2-10h-10l-12%201m48%200l1%202%201%203c1%201%204%203%209%204%208%203%209%203%2011%202%203-2%206-2%209-1%205%202%2021-4%2021-9v-2h-26l-26%201m-20%206a642%20642%200%20009%2040l24%201h24l5-17c8-21%208-22%205-20l-6%203c-5%202-6%202-8%207-2%206-10%205-13-1-1-3-2-4-10-6-8-3-9-3-11-2-4%203-12%201-12-4%200-2-4-5-7-5v4m74%204l-12%2037h36l-7-19c-7-21-6-20-9-18h-8m-64%2039v5c1%203%202%204%205%204l6%203c2%203%203%203%2010%203s8%200%2010-3%203-3%207-3%204%200%206-4c3-6%204-6-21-6l-23%201m50%203c-3%208-3%208%207%204%203-1%205-3%206-5l1-3h-12l-2%204m45-2l3%2012c4%2011%205%2012%208%2013l4%203c0%202%201%202%208%202%208%200%208%200%2012-3s7-4%2010-1c4%203%2019-1%2021-5%201-4%200-5-10-5-6%200-7%201-9%203-2%203-6%204-9%203-2%200-6-5-6-8s-18-13-21-12c-3%202-5%201-8-1l-3-1m-85%2021l-3%203%203%203c3%203%204%203%2011%203s7%200%2010-3l4-3-3-3c-3-3-4-3-11-3-8%200-9%200-11%203M31%20297l-4%203%203%203c4%204%206%204%2010-1%202-2%202-2-1-5-4-4-4-4-8%200m45-1l-2%202-4%202h-1c-2-4-8-2-8%203%201%203%206%204%208%202l1-1%204%202h4v-5l-1-6c-1-1-1%200-1%201m153%200l-2%202-4%202h-2c0-2-1-2-3-2s-3%200-3%203v4c0%202%207%201%207%200h1l4%201c3%200%203%200%203-6v-6l-1%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/378f047f24f43b8f935b83062cd8d2de/ca0a1/6sGx6.webp 223w", "/static/378f047f24f43b8f935b83062cd8d2de/1f248/6sGx6.webp 343w"],
                "sizes": "(max-width: 343px) 100vw, 343px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/378f047f24f43b8f935b83062cd8d2de/e92b6/6sGx6.png 223w", "/static/378f047f24f43b8f935b83062cd8d2de/41431/6sGx6.png 343w"],
                "sizes": "(max-width: 343px) 100vw, 343px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/378f047f24f43b8f935b83062cd8d2de/41431/6sGx6.png",
                "alt": "6sGx6",
                "title": "Average time graph",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Average time graph`}</figcaption>{`
  `}</figure></p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "314px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/18f9f960fdf69b374130bb7ff3d4d511/5b158/ubAAQ.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "86.99551569506727%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'348\\'%20viewBox=\\'0%200%20400%20348\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M174%2013c-1%202-2%203-3%201-2-3-8%200-8%204%200%206%209%206%2010%200%201-2%201-2%201%201%200%202%201%203%207%203h7v-3c0-4-2-6-6-6l-5-1-1-1c-1-1-1%200-2%202m50%200l-2%201-5-1c-3%200-3%200-3%205%200%203%200%204%202%204l1-3%201-3%201%203c0%202%201%203%202%203l1-3c0-4%202-4%202%200%201%203%204%204%205%202h1l2%201%201-4c0-4-2-7-3-4%200%201-3-1-3-3s-2-1-3%202m10%205c0%203%200%204%202%204l1-3%201-3%201%203c0%204%203%204%203%200%201-3%202-3%203%200%200%203%203%204%203%201h1c4%205%2011%200%207-5-1-2-6-3-7%200h-2l-7-2h-6v5M21%2038v2l1-1%201-1%201%202v5l-2-1c-1-2-2-1-1%201%200%202%203%202%205%200%201-1%201-1%203%201%204%203%208-4%205-9-1-2-6%200-6%203%200%201%200%202-1%201v-1c0-1-2-4-4-4l-2%202m335%2049l-3%205c0%202-1%202-45%202H165l-2-3c-3-7-12-7-16%200l-2%203H98l-46%201h47c46%200%2047%200%2047%202l3%204v5l-2%207c0%207-3%2012-18%2033-1%201-3%202-9%202h-6l-7-9c-14-19-14-19-13-24%200-4%200-5-2-7-6-6-14-2-14%206%200%204%200%204-10%2015-11%2011-11%2012-15%2012-7%200-11%204-9%2010%201%208%2012%208%2015%201l2-2%2024-1h25l2%203c2%203%202%204%201%207-3%208%207%2014%2013%208%203-3%204-6%202-9s2-9%207-9c4%200%204-1-1%2016l-5%2023c-3%2011-3%2010-8%2010l-6%202c-2%202-4%202-11%202-9%200-9%200-12-3-3-4-8-4-12%200-3%202-4%202-11%202s-8%200-11-4c-6-6-15-3-15%206%200%207%209%2011%2015%206%202-3%203-3%2010-3%208%200%208%200%2011%204%205%204%209%204%2013%200%203-2%204-3%2012-2%207%200%208%200%209%203l5%204c2%200%203%201%203%202%200%202-2%205-3%205l-4%203c-4%203-18%203-21-1-4-4-10-4-14%200-3%204-17%204-21%201-6-8-18-1-15%208%203%207%2010%208%2015%203%204-4%204-4%2011-4s8%200%2010%204c4%205%2010%205%2014%200%202-4%203-4%2010-4%208%200%208%200%2010%204%204%204%2010%205%2014%201%203-3%203-10%200-12-2-3-2-3%200-9l2-9c0-4%201-4%2026-4h25l2%207c3%2012%203%2013%201%2015-3%202-3%208%200%2012%204%203%2010%203%2013-1%204-4%2016-5%2021%200%205%204%206%204%2010%203%206-2%208-9%204-14-2-2-1-9%202-17%202-5%203-5%2024-5%2017%200%2017%200%2022%203l6%205%202%206c1%204%201%204-1%208-5%2010%206%2019%2014%2011%202-3%203-3%2010-3%208%200%208%200%2011%203%204%205%2011%205%2014-1%203-4%202-6%200-9s-2-7%201-16c2-7%203-7%2017-7%2012%200%2012%200%2015-3%204-4%204-8%201-12-6-5-12-3-15%204-1%202-2%203-8%205l-7%202a165%20165%200%20018-33l3-11%201-6h6l6-1h-12l3-11%203-10h4c5%200%2010-4%2010-8%200-3-5-8-8-8-2%200-2%200-2-3l1-4%203-3c7-3%208-12%202-15-5-3-8-2-11%201M10%2091l-1%202h2c0-2%201-2%202-1l-2%204c-4%203-4%204%205%204l7-1-3-1%201-2c2-2%202-5%200-6h-4c-2%202-2%202-3%201-2-2-2-2-4%200m154%205l-2%204-2%203c3%209%203%209%2010%2011%2010%203%2012%203%2014%202%205-4%2012-2%2014%204%201%203%203%203%2013%207%205%201%206%201%208-1%204-3%207-3%2010%200%203%202%204%202%2015-1l6-2%202-7%202-7-2-3c-2-1-2-3-2-6l1-5h-44l-43%201m103%202c1%204%200%206-2%2010-1%202-1%203%201%209%203%208%202%208%2012%2012%207%203%208%203%2010%202%205-4%2012-1%2013%204%201%204%2018%208%2022%205l7-1c5%201%2020-11%2022-17l3-9c2-9%202-11%200-15l-1-3h-88l1%203m-103%2019l4%2016%203%2015h73l2-10%202-10-8%202c-6%201-7%202-8%205-2%204-4%205-8%205s-8-3-8-6c0-2-2-3-8-5l-9-2-4%201c-5%204-11%202-13-3-1-3-1-4-7-5a2268%202268%200%2001-11-3m-13%206a165%20165%200%2000-7%2024l11%201c10%200%2011%200%2011-2l-6-22c-1-4-1-4-5-4-2%200-3%200-4%203m-81%209c-10%2011-13%2014-10%2016h48l-5-8c-14-20-13-19-18-19-3%200-4%200-15%2011m183%200l-4%2016a84%2084%200%200019-1l-2-9-2-8h-5l-6%202m17%200l3%2012%201%204h23c20%200%2022%200%2021-1l-10-4h-9c-6%204-14%201-14-5%200-1-2-3-7-5-7-3-10-4-8-1m78%201l-13%2010%201%203c0%202%201%202%205%202h5l2-8v-7m-204%2017l-2%207a1226%201226%200%2001-10%2040l7-3c6-4%207-5%207-7%201-5%205-8%209-8s9%205%209%208c0%202%201%203%207%206%204%203%207%203%207%203l-6-24-6-22-10-1-12%201m29%200l5%2024%206%2023%204%201c4%200%205%200%207%202%204%203%2019%203%2024%200%203-2%205-2%207-1h4a2340%202340%200%200112-49h-69m76%200a253%20253%200%2000-10%2034l-3%2012v3l3-1a8923%208923%200%200111-7c0-4%206-8%2010-8%204%201%207%204%207%207l2%204%2014%208-2-10a2254%202254%200%2001-11-37l-1-5-10-1-10%201m26%200l3%2011%208%2032c2%2010%201%209%2018%209%2015%200%2015%200%2018-3%202-2%203-2%207-2l4%201%202-5a641%20641%200%20009-43c-1-2-7-1-10%202l-6%203-6-3-3-3h-22l-22%201m-133%2047l-6%204%2020%201%2019-1-6-4c-7-3-7-3-11-1h-6c-4-2-3-2-10%201m54%2014l-5%204c-3%200-2%207%201%207l4%203c4%204%2019%204%2021%201l4-4c5-2%205-5%200-7-2-1-4-2-4-4l-4-2-9-1c-5%200-6%200-8%203m114%200l-9%203c-1%203-3%205-5%205-3%200-3%202%200%202l4%204c3%203%2018%204%2020%201l5-4%203-2%201-4c0-2%200-2-2-2l-5-2-4-3-8%202M51%20257l157%201a4644%204644%200%20000-2l-157%201M7%20325l-3%203%203%203c4%204%207%204%2011%200l3-3-3-3c-4-4-7-4-11%200m51-2v1l-2%201-4%202c-2%202-2%202-3%200-3-4-9-1-8%204s7%205%209%201l1-1c0%204%207%205%209%202h2c3%204%207%202%207-3s-4-6-7-3c-2%201-2%201-2-2s-2-5-2-2m96%202l-3%203%203%203c4%204%206%204%2010%200l3-3-3-3c-4-4-6-4-10%200m70-2c0%201-1%202-3%202s-3%201-4%204l-1%204v-4c0-3-1-3-3-3-3%200-4%200-4%203v5c2%201%208%201%208-1h2c1%202%203%202%204%201%203%200%203-1%203-6l-1-6-1%201m-45%202v2l1%204c0%203%202%205%203%203h4c2%201%202%201%204-1%204-4-1-11-5-7-2%201-2%201-3-1-2-2-2-2-4%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/18f9f960fdf69b374130bb7ff3d4d511/ca0a1/ubAAQ.webp 223w", "/static/18f9f960fdf69b374130bb7ff3d4d511/715b3/ubAAQ.webp 314w"],
                "sizes": "(max-width: 314px) 100vw, 314px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/18f9f960fdf69b374130bb7ff3d4d511/e92b6/ubAAQ.png 223w", "/static/18f9f960fdf69b374130bb7ff3d4d511/5b158/ubAAQ.png 314w"],
                "sizes": "(max-width: 314px) 100vw, 314px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/18f9f960fdf69b374130bb7ff3d4d511/5b158/ubAAQ.png",
                "alt": "ubAAQ",
                "title": "Total runtime",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Total runtime`}</figcaption>{`
  `}</figure></p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "331px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/0be818bbe7efb844c2c46e494fb45eac/62452/DDUbz.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "82.95964125560539%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'331\\'%20viewBox=\\'0%200%20400%20331\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M126%2014v6h4c5%200%206-1%206-6%200-4-2-6-7-6h-3v6m60-4l-2%201-3%202h-2c-2-2-4-2-6-1-2%202-2%202-3%200-2-2-8-1-8%201v3l-1-3c-2-2-8-3-9-1%200%202%200%202-2%201%200-1-3-2-7-2h-6v5c0%205%203%206%203%201l2-3%201%201v3c1%202%206%203%208%201%200-1%201-1%201%201%201%202%204%204%203%201l3-1%203-2c1-2%201-2%201%201s0%204%202%204l1-1%202-2%204-2h1c1%202%206%203%208%201h1l5%201h3v-6l-1-6-2%202m65%200l-1%201h-4c-4%201-6%203-5%206s5%204%208%202h11c2%202%207%201%208-1h1c0%203%203%202%203-1%201-3%202-3%203%200%200%203%200%203%205%203h6v-4c0-5-4-7-7-4l-2%201c-1-2-7-2-8%200h-2c-2-2-4-2-6%200h-3c0-1-1-2-3-2l-2-1-2-1v1m-55%202c-3%202-1%208%203%208l4-1h1c2%202%207%201%208-1l1-2v2c0%203%203%203%203-1%201-4%203-4%203%200%200%202%200%203%202%203%201%200%202-6%200-8s-8-1-8%201%200%202-1%201c0-3-4-3-7-2h-9m27%204c0%203%200%204%202%204l1-3%201-3%201%203c0%202%201%203%202%203l1-1h1c3%202%207%201%208-2%200-5-4-8-7-4h-3c0-1-1-2-4-2s-3%200-3%205M19%2034l-1%203c0%202%200%202%202%202h2c0%202-1%202-3%201-2%200-2%200-1%201%201%202%204%203%206%201h3c3%202%205%201%205-4%200-4-1-4-3-4-1%200-3%200-3%202h-4c-2-1-2-1%200-1l2-1h-5m222%2037v16h-69l-1-4v-5h-8v9h-58a607%20607%200%20001%202h57v50h-57a639%20639%200%20000%202h57v49h-25v-45h-7v45h-24v-38h-7v38H48l26%201%2026%201v50H73c-17%200-26%200-25%201a12284%2012284%200%2000316-1h-4v-36h-6v36h-25v-51h18l19-1h-37v-33h-7v33h-72v-49h58a613%20613%200%20000-2l-58%201V89h58a613%20613%200%20000-2l-58%201V54h-9v17m-70%2043v25h24v-23h7v23h39V89h-70v25m0%2052v24h24v-49h-24v25m31%200v24h39v-49h-39v25m-95%2051v25h24v-51h-24v26m32-25l-1%2025v25h16v-32h9v-18l-12-1-12%201m32%2025v25h24v-51h-24v26m32-25l-1%2025v25h39v-50l-19-1-19%201m47%2025v25h8v-36h7v36h17v-36h7v18l1%2018%201-10v-10h7v20h24v-51h-72v26M14%20303v8l1%206h13v-15c-1-1-13%200-14%201m140%207v6h13v-13h-13v7m143%200v6h13v-13h-13v7m-233-4l-2%201-3%202c-1%202-1%202-3%200-3-4-7-1-7%204%201%203%206%204%208%202l1-1c1%203%208%203%208%200h2c4%204%2010-1%206-5-2-2-5-3-6%200-1%202-2%201-2-2l-1-3-1%202m158%200l-2%201-3%202c-1%201-1%201-2-1-2-2-6-1-7%203s-1%205%204%205l4-1h1l4%201h3v-6c0-6-1-7-2-4m-42%200c-1%203%200%2010%202%2010s2%200%201-2v-4l1%202c0%203%204%205%207%203h2l1%201%201-2c0-3%202-5%203-5l1%204c0%204%202%204%202%200%200-2%201-3%202-3l1%203c0%205%202%204%202-1v-4l-6-1c-4%200-6%201-7%202%200%201-1%201-2-1h-6l-2-1c-2-2-3-2-3-1m156%202l-1%204v4h5c6%200%206%200%206-3%200-2%201-3%202-3l1%203c0%205%202%204%202-1%200-4-1-5-4-5s-3%201-3%206c0%203-2%201-2-2s-1-4-3-4l-3%201m17%200c-2%203-1%2010%202%2011%203%202%205%200%205-4%200-2%200-2%201-1%203%203%207%202%208-1%200-5-4-8-7-4h-2c0-2-5-3-7-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/0be818bbe7efb844c2c46e494fb45eac/ca0a1/DDUbz.webp 223w", "/static/0be818bbe7efb844c2c46e494fb45eac/b962e/DDUbz.webp 331w"],
                "sizes": "(max-width: 331px) 100vw, 331px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/0be818bbe7efb844c2c46e494fb45eac/e92b6/DDUbz.png 223w", "/static/0be818bbe7efb844c2c46e494fb45eac/62452/DDUbz.png 331w"],
                "sizes": "(max-width: 331px) 100vw, 331px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/0be818bbe7efb844c2c46e494fb45eac/62452/DDUbz.png",
                "alt": "DDUbz",
                "title": "Dropped connections",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Dropped connections`}</figcaption>{`
  `}</figure></p>
    <h2 {...{
      "id": "the-conclusion"
    }}>{`The Conclusion`}</h2>
    <p>{`As we can see, node.js proves to be the fastest in this setting by quite a bit - average request handling takes about a second whereas both tornado and django take at least 3. The more troubling thing are the periodic spikes that seem to happen under load where the average request suddenly took 15 seconds to handle.`}</p>
    <p>{`Unfortunately I have no idea why that might be, my suspicion is something with how the redis library talks to the database. Perhaps it's collecting reads and writes and then doing them all in a big batch ... but surely that can't be the case right?`}</p>
    <p>{`The greatest thing is that hey, no matter whether it's fast or slow, apparently all three of these are well adept at handling a bunch of load almost linearly without becoming increasingly slower when you add more. Just don't try running your whole server in a single process, that obviously doesn't work too well.`}</p>
    <p>{`Load balance!`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://themindstorms.blogspot.com/2011/04/nosql-nedis-redis-implemented-with-node.html"
        }}>{`NoSQL: Nedis: Redis Implemented With Node`}</a>{` (themindstorms.blogspot.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.duostack.com/post/4530917803/free-redis-database-for-every-duostack-app"
        }}>{`Redis is now available on Duostack (Node.js/Ruby Platform)`}</a>{` (duostack.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.waxman.me/how-to-build-a-fast-news-feed-in-redis"
        }}>{`How to Build a Fast News Feed in Redis (and Rails)`}</a>{` (waxman.me)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://thechangelog.com/post/2910383164/webdis-a-redis-http-interface-with-json-output"
        }}>{`webdis: HTTP + JSON API for Redis`}</a>{` (thechangelog.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/django-protip-1-a-better-app-structure/swizec/1386"
        }}>{`Django protip #1: A better App structure`}</a>{` (swizec.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=dd20d0b5-9ae6-49ce-884c-50096218731e",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      